// Service for Monthly Attendance and Monthly Report
import { apiClient, API_CONFIG } from '@/api/core';
import type { ApiResponse } from '@/api/shared';
import { buildQueryString } from '@/api/shared';
import type {
  LC_GetMonthlyAttendanceIn,
  LC_GetMonthlyAttendanceOut,
  MonthlyAttendancePage,
  LC_GetAttendanceByRangeIn,
  LC_GetMonthlyReportIn,
  LC_GetMonthlyReportOut,
  MonthlyReportPage,
} from '../types';
import { mapMonthlyAttendancePage, mapMonthlyReportPage } from '../mappers';


export class MonthlyAttendanceService {
  async getMonthlyAttendance(
    input: LC_GetMonthlyAttendanceIn,
  ): Promise<ApiResponse<MonthlyAttendancePage>> {
    const queryString = buildQueryString({
      queryType: 'MONTHLY_CUT',
      monthlyCutCode: input.monthlyCutCode,
      page: input.page,
      pageSize: input.pageSize,
      searchText: input.searchText,
      orderBy: input.orderBy,
      orderDirection: input.orderDirection,
    });

    const response = await apiClient.get<LC_GetMonthlyAttendanceOut>(
      `${API_CONFIG.ENDPOINTS.MONTHLY_REPORT}${queryString}`,
    );
    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }
    return {
      success: true,
      data: mapMonthlyAttendancePage(response.data),
      status: response.status,
    };
  }

  async getAttendanceByRange(
    input: LC_GetAttendanceByRangeIn,
  ): Promise<ApiResponse<MonthlyAttendancePage>> {
    const queryString = buildQueryString({
      queryType: 'DATE_RANGE',
      dateFrom: input.dateFrom,
      dateTo: input.dateTo,
      page: input.page,
      pageSize: input.pageSize,
      searchText: input.searchText,
      orderBy: input.orderBy,
      orderDirection: input.orderDirection,
    });

    const response = await apiClient.get<LC_GetMonthlyAttendanceOut>(
      `${API_CONFIG.ENDPOINTS.MONTHLY_REPORT}${queryString}`,
    );
    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }
    return {
      success: true,
      data: mapMonthlyAttendancePage(response.data),
      status: response.status,
    };
  }

  async getMonthlyReport(
    input: LC_GetMonthlyReportIn,
  ): Promise<ApiResponse<MonthlyReportPage>> {
    const queryString = buildQueryString({ ...input });
    const endpoint = `${API_CONFIG.ENDPOINTS.MONTHLY_REPORT}${queryString}`;

    const response = await apiClient.get<LC_GetMonthlyReportOut>(endpoint);

    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }
    return {
      success: true,
      data: mapMonthlyReportPage(response.data),
      status: response.status,
    };
  }
}

export const monthlyAttendanceService = new MonthlyAttendanceService();
